import { supabase } from "./supabase";
import type {
  AttributeDefinition,
  ContactAttributeValue,
  CreateAttributeDefinitionDTO,
  UpdateAttributeDefinitionDTO,
  AttributeValueInput,
} from "../types/contactAttribute.types";
import type { UUID } from "../types/common.types";

function generateSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "");
}

export async function getAttributeDefinitions(): Promise<
  AttributeDefinition[]
> {
  const { data, error } = await supabase
    .from("contact_attribute_definitions")
    .select("*")
    .order("display_order")
    .order("name");

  if (error) {
    throw error;
  }
  return (data || []) as AttributeDefinition[];
}

export async function createAttributeDefinition(
  input: CreateAttributeDefinitionDTO,
): Promise<AttributeDefinition> {
  const slug = input.slug || generateSlug(input.name);
  const { data, error } = await supabase
    .from("contact_attribute_definitions")
    .insert({ ...input, slug })
    .select()
    .single();

  if (error) {
    throw error;
  }
  return data as AttributeDefinition;
}

export async function updateAttributeDefinition(
  id: UUID,
  updates: UpdateAttributeDefinitionDTO,
): Promise<AttributeDefinition> {
  const { data, error } = await supabase
    .from("contact_attribute_definitions")
    .update(updates)
    .eq("id", id)
    .select()
    .single();

  if (error) {
    throw error;
  }
  return data as AttributeDefinition;
}

export async function deactivateAttributeDefinition(id: UUID): Promise<void> {
  const { error } = await supabase
    .from("contact_attribute_definitions")
    .update({ is_active: false })
    .eq("id", id);

  if (error) {
    throw error;
  }
}

export async function getContactAttributeValues(
  contactId: UUID,
): Promise<ContactAttributeValue[]> {
  const { data, error } = await supabase
    .from("contact_attribute_values")
    .select("*, attribute:contact_attribute_definitions(*)")
    .eq("contact_id", contactId)
    .order("sort_order")
    .order("created_at");

  if (error) {
    throw error;
  }
  return (data || []) as ContactAttributeValue[];
}

export async function upsertContactAttributeValue(
  contactId: UUID,
  attributeId: UUID,
  value: AttributeValueInput,
): Promise<void> {
  const { error } = await supabase.rpc("upsert_contact_attribute_value", {
    p_contact_id: contactId,
    p_attribute_id: attributeId,
    p_value_text: value.value_text ?? null,
    p_value_number: value.value_number ?? null,
    p_value_boolean: value.value_boolean ?? null,
    p_value_date: value.value_date ?? null,
  });

  if (error) {
    throw error;
  }
}

export async function clearContactAttributeValue(
  contactId: UUID,
  attributeId: UUID,
): Promise<void> {
  const { error } = await supabase.rpc("delete_contact_attribute_value", {
    p_contact_id: contactId,
    p_attribute_id: attributeId,
  });

  if (error) {
    throw error;
  }
}

export async function addContactAttributeValue(
  contactId: UUID,
  attributeId: UUID,
  value: AttributeValueInput,
  sortOrder = 0,
): Promise<void> {
  const { error } = await supabase.from("contact_attribute_values").insert({
    contact_id: contactId,
    attribute_id: attributeId,
    value_text: value.value_text ?? null,
    value_number: value.value_number ?? null,
    value_boolean: value.value_boolean ?? null,
    value_date: value.value_date ?? null,
    sort_order: sortOrder,
  });

  if (error) {
    throw error;
  }
}

export async function removeContactAttributeValue(
  valueId: UUID,
): Promise<void> {
  const { error } = await supabase
    .from("contact_attribute_values")
    .delete()
    .eq("id", valueId);

  if (error) {
    throw error;
  }
}
